import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import Contact from '@repo/utils/models/Contact';
import { Section } from '@repo/components/containers';
import Container from '@components/ContentContainer';
import { List as VirtualizedList, createListContext } from '@components/List/VirtualizedList';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContactSelecting, useSaveViewState, useOnVisible } from '@repo/hooks';
import { useScrollRestoration } from '@components/utils/use-scroll-restoration';
import styles from '@views/Activity/Activity.module.scss';
import rowStyles from '@views/Activity/ActivityListItem/ActivityListItem.module.scss';
import { selectSavedViewState } from '@repo/redux/actions/ui';
import { TimeSeparator } from '@components/List/VirtualizedList/utils/time-separators';
import { fetchNexusStatus } from '@repo/redux/actions/nexus';
import { ActivityItem } from '@repo/utils/models/Activity';
import { ID, ViewID } from '@repo/utils/types';
import useEnabledNotifications from '@views/Settings/panels/Notifications/use-enabled-notifications';
import ActivityListItem from '@views/Activity/ActivityListItem';
import { ActivitySidebar } from '@views/Activity/ActivitySidebar';
import ClearedActivity from '@views/Activity/ClearedActivity';
import GroupActivityHeader from './GroupActivityHeader';
import {
  selectActiveGroupActivityFilter,
  selectGroupActivity,
} from '@repo/redux/reducers/data/groupActivity';
import { fetchGroupActivity, fetchMoreGroupActivity } from '@repo/redux/actions/groupActivity';
import { selectGroupMemberIDs } from '@repo/redux/actions/groups';

export interface ActivityViewState {
  activeActivityItem?: ActivityItem;
  activeNested: boolean;
  nestedItem?: Contact;
}

type ActivityViewAction =
  | ['focusMainList']
  | ['focusNestedList', Contact]
  | ['selectActiveItem', ActivityItem]
  | ['clearSelections'];

const activityViewReducer = (
  state: ActivityViewState,
  [type, value]: ActivityViewAction,
): ActivityViewState => {
  switch (type) {
    case 'selectActiveItem':
      return { ...state, activeActivityItem: value, activeNested: false, nestedItem: null };

    case 'focusMainList':
      return { ...state, activeNested: false, nestedItem: null };

    case 'focusNestedList':
      return { ...state, activeNested: true, nestedItem: value };

    case 'clearSelections':
      return { ...state, activeActivityItem: null, activeNested: false, nestedItem: null };

    default:
      return state;
  }
};

const ActivityListContext = createListContext();

export default function GroupActivity() {
  const dispatchToStore = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams();
  const viewID = `GroupActivity_${id}` as ViewID;

  const { enabledNotifications } = useEnabledNotifications();
  const scrollingContainerRef = useRef(null);
  const listRef = useRef(null);
  const activity = useSelector(selectGroupActivity);
  const activityFilter = useSelector(selectActiveGroupActivityFilter);
  const savedViewState = useSelector(selectSavedViewState(viewID));
  const [resetOnNextUpdate, setResetOnNextUpdate] = useState(false);
  const viewScrolledAtMount = useScrollRestoration({ viewID, scrollingContainerRef });
  const groupMembers = useSelector(selectGroupMemberIDs(id));

  const [{ activeActivityItem, activeNested, nestedItem }, dispatchToComponent] = useReducer(
    activityViewReducer,
    {
      activeActivityItem: savedViewState?.activeActivityItem || null,
      activeNested: false, //savedViewState?.activeNested || false,
      nestedItem: null, //savedViewState?.nestedItem || null,
    },
  );

  const activityItems = useMemo(() => activity.sortedItems, [activity.items]);
  const multiplePeoplePerReconnect = enabledNotifications['activity_group_reconnects'];

  // TODO: this is hacky as shit
  // the correct size should be getting determined by item
  useEffect(() => {
    if (resetOnNextUpdate) {
      setResetOnNextUpdate(false);
      listRef.current?.resetAfterIndex(0);
    }
  }, [activity.updated]);

  useSaveViewState(viewID, { activeActivityItem, activeNested, nestedItem }, [
    activeActivityItem,
    activeNested,
    nestedItem,
  ]);
  useAppContactSelecting({
    contact: (activeActivityItem?.type !== 'shared' && nestedItem) || activeActivityItem?.contact,
    source: `activity${nestedItem ? '-nested' : '-'}list-item`,
    additionalDeps: [activeNested],
  });

  useEffect(() => {
    !viewScrolledAtMount && dispatchToStore(fetchGroupActivity(id));
    dispatchToStore(fetchNexusStatus());
  }, []);

  useEffect(() => {
    if (groupMembers.length > 0) {
      dispatchToStore(fetchGroupActivity(id));
    }
  }, [groupMembers.length]);

  useOnVisible(() => {
    if (!activeActivityItem || activity.items.keySeq().indexOf(activeActivityItem.id) < 10) {
      dispatchToStore(fetchGroupActivity(id));
    }
  }, [activeActivityItem]);

  const defaultSelectedIdx = useMemo(() => {
    let idx = 0;
    if (savedViewState?.activeActivityItem) {
      idx = activity.items
        .keySeq()
        .findIndex((key) => key == savedViewState.activeActivityItem?.id);
    }
    return idx;
  }, []);

  const itemBasedListRowClassName = (id: ID) =>
    cn(activity.items.get(id)?.contact && rowStyles.selectableListItem);

  const handleContactSelect = (contact: Contact) => {
    contact && navigate(contact.link);
  };

  const handleItemSelect = (activityItem: ActivityItem) => {
    if (activityItem?.contact) {
      return handleContactSelect(activityItem.contact);
    }
  };

  const handleMainListItemHotKey = (item: ActivityItem, e: KeyboardEvent) => {
    // right arrow nav into nested list
    if (['Tab', 'ArrowRight'].includes(e.key) && item?.hasContactList && !activeNested) {
      e.preventDefault();
      return dispatchToComponent(['focusNestedList', undefined]);
    }
  };

  const handleNestedListItemHotKey = (contact: Contact, e: KeyboardEvent) => {
    // left arrow nav back to main list
    if (contact && e.key === 'ArrowLeft' && activeNested) {
      e.preventDefault();
      return dispatchToComponent(['focusMainList']);
    }
  };

  const determineItemSize = (item: ActivityItem) => {
    if (item?.type === 'reconnect' && !multiplePeoplePerReconnect) {
      return 58;
    }

    if (
      (item?.type === 'import' && item?.metadata?.importTotal > 1) ||
      ['reconnect', 'announcement'].includes(item?.type)
    ) {
      return 86;
    }

    if (item?.type === 'news') {
      return 132;
    }

    if (
      item?.type === 'post' &&
      (item?.metadata?.source !== 'linkedin' || item?.metadata?.postSubtype !== 'announcement')
    ) {
      return 108;
    }

    if (item?.metadata?.subtype === 'bio') {
      return 108;
    }

    if (item?.type === 'diff') {
      return 86;
    }

    return 58;
  };

  const renderActivityItems = () => {
    if (activity.isLoaded && activity.items.size === 0) {
      return <ClearedActivity selectAppliedFilter={selectActiveGroupActivityFilter} />;
    }

    return (
      <VirtualizedList
        key={String(activityFilter)}
        active={!activeNested}
        items={activityItems}
        ListContext={ActivityListContext}
        listRef={listRef}
        ref={scrollingContainerRef}
        shouldResetHighlight={false}
        canLoadMore={activity.canLoadMore}
        loadMore={() => dispatchToStore(fetchMoreGroupActivity(id))}
        onItemHighlight={(item: ActivityItem) => dispatchToComponent(['selectActiveItem', item])}
        // @ts-ignore
        onHotKey={handleMainListItemHotKey}
        listRowClassName={rowStyles.mainListItem}
        itemBasedListRowClassName={itemBasedListRowClassName}
        determineItemSize={determineItemSize}
        onItemClick={handleItemSelect}
        renderItem={(item: ActivityItem, isHighlighted) => {
          if ((item as unknown as TimeSeparator).type === 'time-separator') {
            return (
              <div className={styles.timeSeparator}>{(item as unknown as TimeSeparator).label}</div>
            );
          }

          return (
            <ActivityListItem key={item.id} item={item} selected={isHighlighted} noDismissal />
          );
        }}
        timeBucketKey="date"
        defaultHighlightedIdx={defaultSelectedIdx}
        disabled={activity.isDeleting}
      />
    );
  };

  return (
    <Container key={String(multiplePeoplePerReconnect)}>
      {/* Adapt header for use on Group */}
      <Container.Primary className={styles.primary} id="content" header={<GroupActivityHeader />}>
        <Section>{renderActivityItems()}</Section>
      </Container.Primary>
      <ActivitySidebar
        activityItemID={activeActivityItem?.id}
        setActiveNested={(contact: Contact) => dispatchToComponent(['focusNestedList', contact])}
        active={activeNested}
        onHotKey={handleNestedListItemHotKey}
        onSelect={handleContactSelect}
        focusMainList={() => dispatchToComponent(['focusMainList'])}
        noDismissal
      />
    </Container>
  );
}
